import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Package,
  Receipt,
  CreditCard,
  Users,
  LogOut,
  Menu,
  X,
  Settings,
  Truck,
  FileText,
  TrendingUp,
  UserCheck,
  Monitor,
  Folder,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Sliders,
  Key,
  ShoppingBag,
  ClipboardList,
  DollarSign,
  Tag,
  ScrollText,
  Banknote,
  ShieldCheck,
} from 'lucide-react';

const Layout = ({ children, user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [businessConfig, setBusinessConfig] = useState({
    sistema_nombre: 'LICORSTORE',
    slogan: 'Sistema de Gestión de Licorería'
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    fetchBusinessConfig();
  }, []);

  // Recargar configuración cuando se navega (especialmente desde /parameters)
  useEffect(() => {
    fetchBusinessConfig();
  }, [location.pathname]);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig({
        sistema_nombre: response.data.sistema_nombre || 'LICORSTORE',
        slogan: response.data.slogan || 'Sistema de Gestión de Licorería'
      });
    } catch (error) {
      console.error('Error al cargar configuración del sistema');
    }
  };


  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Continuar con logout local aunque falle el backend
      console.error('Error al cerrar sesión en servidor:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'admin', 'cajero', 'inventario', 'gerente', 'contador'] },
    { path: '/pos', icon: Monitor, label: 'Punto de Venta', roles: ['superadmin', 'admin', 'cajero'] },
    {
      id: 'facturacion',
      label: 'Facturación',
      icon: Receipt,
      roles: ['superadmin', 'admin', 'cajero', 'gerente', 'contador'],
      submenu: [
        { path: '/invoices', icon: Receipt, label: 'Facturas', roles: ['superadmin', 'admin', 'cajero', 'gerente', 'contador'] },
        { path: '/quotations', icon: FileText, label: 'Cotizaciones', roles: ['superadmin', 'admin', 'cajero', 'gerente'] },
        { path: '/devoluciones', icon: FileText, label: 'Devoluciones', roles: ['superadmin', 'admin', 'cajero', 'gerente'] },
      ]
    },
    { 
      id: 'inventario',
      icon: Package, 
      label: 'Inventario', 
      roles: ['superadmin', 'admin', 'cajero', 'inventario', 'gerente'],
      submenu: [
        { path: '/products', icon: Package, label: 'Productos', roles: ['superadmin', 'admin', 'cajero', 'inventario', 'gerente'] },
        { path: '/categories', icon: Folder, label: 'Categorías', roles: ['superadmin', 'admin', 'inventario', 'gerente'] },
        { path: '/inventory-adjustments', icon: ClipboardList, label: 'Ajustes de Inventario', roles: ['superadmin', 'admin', 'inventario', 'gerente'] },
        { path: '/price-changes', icon: DollarSign, label: 'Cambio de Precios', roles: ['superadmin', 'admin', 'gerente'] },
        { path: '/price-lists', icon: DollarSign, label: 'Listas de Precios', roles: ['superadmin', 'admin', 'gerente'] },
        { path: '/label-printing', icon: Tag, label: 'Impresión de Etiquetas', roles: ['superadmin', 'admin', 'inventario', 'cajero'] },
        { path: '/ofertas', icon: Tag, label: 'Ofertas', roles: ['superadmin', 'admin', 'gerente'] },
      ]
    },
    { 
      id: 'clientes',
      icon: UserCheck, 
      label: 'Clientes', 
      roles: ['superadmin', 'admin', 'cajero', 'gerente', 'contador'],
      submenu: [
        { path: '/customers', icon: UserCheck, label: 'Clientes', roles: ['superadmin', 'admin', 'cajero', 'gerente', 'contador'] },
        { path: '/accounts-receivable', icon: CreditCard, label: 'Cuentas por Cobrar', roles: ['superadmin', 'admin', 'cajero', 'contador'] },
        { path: '/estado-cuenta', icon: FileText, label: 'Estado de Cuenta', roles: ['superadmin', 'admin', 'cajero', 'contador'] },
      ]
    },
    { 
      id: 'usuarios',
      icon: Users, 
      label: 'Usuarios', 
      roles: ['superadmin', 'admin', 'cajero', 'inventario', 'gerente', 'contador'],
      submenu: [
        { path: '/users', icon: Users, label: 'Gestión de Usuarios', roles: ['superadmin', 'admin', 'gerente'] },
        { path: '/change-password', icon: Key, label: 'Cambiar Contraseña', roles: ['superadmin', 'admin', 'cajero', 'inventario', 'gerente', 'contador'] },
      ]
    },
    { 
      id: 'caja',
      icon: CreditCard,
      label: 'Caja',
      roles: ['superadmin', 'admin', 'cajero', 'gerente'],
      submenu: [
        { path: '/cash-register', icon: CreditCard, label: 'Caja', roles: ['superadmin', 'admin', 'cajero', 'gerente'] },
        { path: '/cobro-facturas', icon: Banknote, label: 'Cobro de Facturas', roles: ['superadmin', 'admin', 'cajero', 'gerente'] },
      ]
    },
    { path: '/suppliers', icon: Truck, label: 'Proveedores', roles: ['superadmin', 'admin', 'contador'] },
    { path: '/purchases', icon: ShoppingBag, label: 'Compras', roles: ['superadmin', 'admin'] },
    { path: '/accounts-payable', icon: FileText, label: 'Cuentas por Pagar', roles: ['superadmin', 'admin', 'contador'] },
    { 
      id: 'reportes',
      icon: TrendingUp, 
      label: 'Reportes', 
      roles: ['superadmin', 'admin', 'gerente', 'contador', 'inventario', 'cajero'],
      submenu: [
        { path: '/reports', icon: TrendingUp, label: 'Reportes Generales', roles: ['superadmin', 'admin', 'gerente', 'contador'] },
        { path: '/reports/607', icon: Receipt, label: 'Ventas 607 (NCF/e-CF)', roles: ['superadmin', 'admin', 'gerente', 'contador', 'inventario', 'cajero'] },
        { 
          id: 'reportes-inventario',
          icon: Package, 
          label: 'Inventario', 
          roles: ['superadmin', 'admin', 'gerente', 'inventario'],
          submenu: [
            { path: '/reports/inventory', icon: Package, label: 'Stock Bajo / Sin Existencia', roles: ['superadmin', 'admin', 'gerente', 'inventario'] },
          ]
        },
      ]
    },
    { path: '/comprobantes-fiscales', icon: FileText, label: 'Comprobantes Fiscales', roles: ['superadmin', 'admin', 'contador'] },
    { path: '/certificados', icon: ShieldCheck, label: 'Certificados', roles: ['superadmin'] },
    { path: '/config', icon: Settings, label: 'Configuración', roles: ['superadmin', 'admin'] },
    { path: '/parameters', icon: Sliders, label: 'Parámetros', roles: ['superadmin'] },
    { path: '/system-reset', icon: AlertTriangle, label: 'Reset Sistema', roles: ['superadmin'] },
    { path: '/system-logs', icon: ScrollText, label: 'Logs del Sistema', roles: ['superadmin'] },
  ];

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Check if current path is in submenu (including nested submenus)
  const isSubmenuActive = (submenu) => {
    if (!submenu) return false;
    return submenu.some(item => {
      if (item.path && location.pathname === item.path) return true;
      if (item.submenu) return isSubmenuActive(item.submenu);
      return false;
    });
  };

  // Auto-expand menu if submenu item is active (including nested)
  useEffect(() => {
    menuItems.forEach(item => {
      if (item.submenu && isSubmenuActive(item.submenu)) {
        setExpandedMenus(prev => ({ ...prev, [item.id]: true }));
        // Also expand nested submenus if active
        item.submenu.forEach(subItem => {
          if (subItem.submenu && isSubmenuActive(subItem.submenu)) {
            setExpandedMenus(prev => ({ ...prev, [subItem.id]: true }));
          }
        });
      }
    });
  }, [location.pathname]);

  const filteredMenuItems = user
    ? menuItems.filter((item) => item.roles.includes(user.role))
    : [];

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`${
          isMobile
            ? sidebarOpen
              ? 'translate-x-0'
              : '-translate-x-full'
            : sidebarOpen
            ? 'w-64'
            : 'w-20'
        } ${
          isMobile ? 'fixed inset-y-0 left-0 z-50 w-64' : 'relative'
        } bg-gradient-to-b from-slate-900 to-slate-800 text-white transition-all duration-300 ease-in-out flex flex-col shadow-2xl`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 md:p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            {(sidebarOpen || !isMobile) && (
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-emerald-400" data-testid="app-logo">
                  {businessConfig.sistema_nombre}
                </h1>
                <p className="text-xs text-slate-400 mt-1">{businessConfig.slogan}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              data-testid="sidebar-toggle-btn"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 md:p-4 space-y-2 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            
            // Handle items with submenu
            if (item.submenu) {
              const isExpanded = expandedMenus[item.id];
              const hasActiveSubmenu = isSubmenuActive(item.submenu);
              
              return (
                <div key={item.id}>
                  {/* Parent Menu Item */}
                  <div
                    onClick={() => toggleSubmenu(item.id)}
                    className={`flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all duration-200 cursor-pointer ${
                      hasActiveSubmenu
                        ? 'bg-emerald-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} />
                      {(sidebarOpen || !isMobile) && <span className="font-medium text-sm md:text-base">{item.label}</span>}
                    </div>
                    {(sidebarOpen || !isMobile) && (
                      isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                    )}
                  </div>
                  
                  {/* Submenu Items */}
                  {isExpanded && (sidebarOpen || !isMobile) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.submenu
                        .filter(subItem => subItem.roles.includes(user.role))
                        .map((subItem) => {
                          const SubIcon = subItem.icon;
                          
                          // Handle nested submenu (level 2)
                          if (subItem.submenu) {
                            const isSubExpanded = expandedMenus[subItem.id];
                            const hasActiveNestedSubmenu = subItem.submenu.some(nested => location.pathname === nested.path);
                            
                            return (
                              <div key={subItem.id}>
                                <div
                                  onClick={() => toggleSubmenu(subItem.id)}
                                  className={`flex items-center justify-between px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-200 cursor-pointer ${
                                    hasActiveNestedSubmenu
                                      ? 'bg-emerald-500 text-white'
                                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <SubIcon size={18} />
                                    <span className="font-medium text-sm">{subItem.label}</span>
                                  </div>
                                  {isSubExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </div>
                                
                                {/* Nested submenu items (level 3) */}
                                {isSubExpanded && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {subItem.submenu
                                      .filter(nestedItem => nestedItem.roles.includes(user.role))
                                      .map((nestedItem) => {
                                        const NestedIcon = nestedItem.icon;
                                        const isNestedActive = location.pathname === nestedItem.path;
                                        return (
                                          <Link
                                            key={nestedItem.path}
                                            to={nestedItem.path}
                                            onClick={() => isMobile && setSidebarOpen(false)}
                                            data-testid={`nav-link-${nestedItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                          >
                                            <div
                                              className={`flex items-center gap-3 px-3 md:px-4 py-2 rounded-lg transition-all duration-200 ${
                                                isNestedActive
                                                  ? 'bg-emerald-400 text-white shadow-lg'
                                                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                                              }`}
                                            >
                                              <NestedIcon size={16} />
                                              <span className="font-medium text-xs">{nestedItem.label}</span>
                                            </div>
                                          </Link>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Regular submenu item (level 2)
                          const isActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => isMobile && setSidebarOpen(false)}
                              data-testid={`nav-link-${subItem.label.toLowerCase()}`}
                            >
                              <div
                                className={`flex items-center gap-3 px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? 'bg-emerald-500 text-white shadow-lg'
                                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                }`}
                              >
                                <SubIcon size={18} />
                                <span className="font-medium text-sm">{subItem.label}</span>
                              </div>
                            </Link>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle regular menu items
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => isMobile && setSidebarOpen(false)}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                <div
                  className={`flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-500 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  {(sidebarOpen || !isMobile) && <span className="font-medium text-sm md:text-base">{item.label}</span>}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-3 md:p-4 border-t border-slate-700">
          <div className="flex items-center gap-2 md:gap-3 mb-3">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-sm flex-shrink-0">
              {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            {(sidebarOpen || !isMobile) && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate" data-testid="user-full-name">{user?.full_name}</p>
                <p className="text-xs text-slate-400 capitalize" data-testid="user-role">{user?.role}</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Button
              variant="outline"
              onClick={() => navigate('/change-password')}
              className={`w-full bg-slate-700 hover:bg-emerald-600 border-slate-600 text-white text-sm ${
                !sidebarOpen && !isMobile && 'px-0'
              }`}
            >
              <Key size={18} />
              {(sidebarOpen || !isMobile) && <span className="ml-2">Cambiar Contraseña</span>}
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className={`w-full bg-slate-700 hover:bg-red-600 border-slate-600 text-white text-sm ${
                !sidebarOpen && !isMobile && 'px-0'
              }`}
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              {(sidebarOpen || !isMobile) && <span className="ml-2">Cerrar Sesión</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto ${isMobile ? 'w-full' : ''}`}>
        {/* Mobile Header */}
        {isMobile && (
          <div className="sticky top-0 z-30 bg-white border-b border-slate-200 p-4 flex items-center justify-between shadow-sm">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="text-slate-600"
            >
              <Menu size={24} />
            </Button>
            <h2 className="text-lg font-bold text-slate-800">{businessConfig.sistema_nombre}</h2>
            <div className="w-10"></div>
          </div>
        )}
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
